import { FOURGT_CODE } from '@apps-common//config/product-codes'
import { IUnitPingResultModel } from '@apps-common/types/portal/unit-control'

import { calcSignalStrength } from './helpers'

// status consts
export const STATUS_ONLINE = 'online'
export const STATUS_OFFLINE = 'offline'

// output type consts
export const OUTPUT_OPEN = 'off'
export const OUTPUT_CLOSED = 'on'

// sensor type consts
export const SENSOR_TYPE_TEMP = 'temp'

// sensor status consts
export const SENSOR_STATUS_ENABLED = 'enabled'
export const SENSOR_STATUS_DISABLED = 'disabled'

// sensor state consts
export const SENSOR_STATE_ONLINE = 'online'
export const SENSOR_STATE_TROUBLE = 'trouble'

// sensor alarm consts
export const SENSOR_HIGH_ALARM = 'E'
export const SENSOR_HIGH_RESTORE = 'R'
export const SENSOR_LOW_ALARM = 'E'
export const SENSOR_LOW_RESTORE = 'R'

export interface ISensorStatus {
  status: string
  state: string
  lowAlarm: string
  highAlarm: string
}

export interface IStatusSensor {
  index: number
  type: string
  status: ISensorStatus
  value: string
}

export interface IStatusOutput {
  index: number
  state: string
}

export interface IStatusModel {
  imageUrl: string | null
  name: string
}

export interface IUnitStatus {
  online: boolean
  model: IStatusModel
  outputs: IStatusOutput[]
  sensors: IStatusSensor[]
  signal: number | undefined
  accountNo: string
}

const flipHexString = (hexValue: string, hexDigits: number) => {
  let h = hexValue.substr(0, 2)

  for (let i = 0; i < hexDigits; ++i) {
    h += hexValue.substr(2 + (hexDigits - 1 - i) * 2, 2)
  }

  return h
}

const hexToFloat = (hex: any) => {
  // eslint-disable-next-line no-bitwise
  const s = hex >> 31 ? -1 : 1
  // eslint-disable-next-line no-bitwise
  const e = (hex >> 23) & 0xff

  // eslint-disable-next-line no-bitwise, no-restricted-properties, prefer-exponentiation-operator
  return ((s * ((hex & 0x7fffff) | 0x800000) * 1.0) / Math.pow(2, 23)) * Math.pow(2, e - 127)
}

const getSensorStatus = (statuses: number): ISensorStatus => {
  const bits = statuses.toString(2).padStart(4, '0')

  let status = SENSOR_STATUS_DISABLED
  let state = SENSOR_STATE_TROUBLE
  let high = SENSOR_HIGH_RESTORE
  let low = SENSOR_LOW_RESTORE

  const bitArr = bits.split('').reverse()

  for (let i = 0; i < bitArr.length; i++) {
    const bit = bitArr[i]

    if (i === 0) {
      if (bit === '1') status = SENSOR_STATUS_ENABLED
    } else if (i === 1) {
      if (bit === '1') state = SENSOR_STATE_ONLINE
    } else if (i === 2) {
      if (bit === '1') high = SENSOR_HIGH_ALARM
    } else if (i === 3) {
      if (bit === '1') low = SENSOR_LOW_ALARM
    }
  }

  const sensorStatus: ISensorStatus = {
    status,
    state,
    highAlarm: high,
    lowAlarm: low
  }

  return sensorStatus
}

export class DeviceStatus {
  private keyValues: string[]

  // private unit: IUnitGenericDetailsModel
  // private unitAccountNo: string

  private productId: string

  public online: boolean

  public model: IStatusModel

  public outputs: IStatusOutput[]

  public sensors: IStatusSensor[]

  public signal: number | undefined

  public accountNo: string

  constructor(unitAccountNo: string, unitProductId: string, unitProduct: string, unitImgUrl: string, data: IUnitPingResultModel) {
    this.keyValues = []
    // this.unit = unit
    // this.unitAccountNo = unitAccountNo
    this.productId = unitProductId
    this.online = data.pingResult
    this.model = {
      imageUrl: unitImgUrl,
      name: unitProduct
    }
    this.outputs = []
    this.sensors = []
    this.signal = undefined
    this.accountNo = unitAccountNo

    this.buildStatus(data)
  }

  public status(): IUnitStatus {
    return {
      online: this.online,
      model: this.model,
      outputs: this.outputs,
      sensors: this.sensors,
      signal: this.signal,
      accountNo: this.accountNo
    }
  }

  private buildStatus(data: IUnitPingResultModel) {
    if (data) {
      if (data?.extraInfo?.length > 0) {
        this.keyValues = data.extraInfo.split(',')
      }
    }

    this.setOutputs()
    this.setSensors()
    this.setSignal()
    this.setAccountNo()
  }

  private setOutputs() {
    const oKeyValue = this.keyValues.find((kv) => kv.startsWith('O'))

    if (oKeyValue) {
      const keyValue = oKeyValue.split(':')

      if (keyValue && keyValue.length === 2) {
        const outputValues = keyValue[1]

        for (let i = 0; i < outputValues.length; i++) {
          const state = outputValues.charAt(i) === '1' ? OUTPUT_CLOSED : OUTPUT_OPEN
          const output: IStatusOutput = {
            index: i,
            state
          }

          this.outputs.push(output)
        }
      }
    }
  }

  private setSensors() {
    const sKeyValue = this.keyValues.find((kv) => kv.startsWith('SEN'))

    if (sKeyValue) {
      try {
        const keyValue = sKeyValue.split(':')

        if (keyValue && keyValue.length === 2) {
          const sensorStrs = keyValue[1]

          if (sensorStrs && sensorStrs.length > 0) {
            const sensorValues = sensorStrs.match(/.{9}/g)

            if (sensorValues) {
              for (let i = 0; i < sensorValues.length; i++) {
                const sensorValue = sensorValues[i]
                const status = parseInt(sensorValue.slice(0, 1), 10)
                const rawValue = sensorValue.slice(1, sensorValue.length)
                const hexValue = flipHexString(`0x${rawValue}`, 8)
                const floatValue = hexToFloat(hexValue)
                const sensor: IStatusSensor = {
                  index: i,
                  type: SENSOR_TYPE_TEMP,
                  status: getSensorStatus(status),
                  value: floatValue.toFixed(1)
                }

                this.sensors.push(sensor)
              }
            }
          }
        }
      } catch (error) {
        // do nothing
      }
    }
  }

  private async setSignal() {
    this.signal = -1

    if (this.productId !== FOURGT_CODE) {
      //   const events = await this.unitService.getUnitTraffic(this.ipAddress, 0, 1)
      //   if (events.result === REQUEST_SUCCESS) {
      //     if (events.data && events.data.length > 0) {
      //       try {
      //         const parsedSignal = parseInt(events.data[0].signalStrength)
      //         signal = Math.ceil(((parsedSignal / 30) * 10) / 2)
      //       } catch {
      //         // oops -- something went wrong
      //       }
      //     }
      //   }
    } else {
      const sKeyValue = this.keyValues.find((kv) => kv.includes('RSSI'))

      if (sKeyValue) {
        const keyValue = sKeyValue.split(':')

        if (keyValue && keyValue.length === 2) {
          try {
            const signal = parseInt(keyValue[1], 10)

            if (signal && signal > 0) {
              // convert RSSI to a value out of 10
              this.signal = calcSignalStrength(signal)
            }
          } catch {
            // let fallback be -1 set above
          }
        }
      }
    }
  }

  private setAccountNo() {
    // this.accountNo = this.accountNo

    const aKeyValue = this.keyValues.find((kv) => kv.includes('AccountNo'))

    if (aKeyValue) {
      const keyValue = aKeyValue.split(':')

      if (keyValue && keyValue.length === 2) [, this.accountNo] = keyValue
    }
  }
}
